import React from "react";
import { Typography, Table, Tag } from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  ArrowRightOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { createBaseBlock } from "./BaseBlock";
import remarkGfm from "remark-gfm";
import Markdown from "react-markdown";
import { stripMarkdown } from "../cardUtils";

// --- Data Types ---
export interface InvestmentSummaryData {
  executiveSummary?: string;
  highlights?: string[];
  investments?: {
    epicTitle: string;
    currentHours: number;
    previousHours: number;
    hoursDelta: number;
    currentContributors: number;
    previousContributors: number;
    contributorsDelta: number;
    commentary: string;
  }[];
}

// --- Recipe ---
const investmentSummaryRecipe = {
  id: "investment-summary-block",
  name: "Investment Summary Block",
  globalKey: [],
  prefix: `{
      "executiveSummary": "string - highlights and summarizes the key findings of the investment summary",
      "highlights": [
        "string - 3 key highlights determing by outliers, overall investment volume, or strategic shifts"
      ],
      "investments":
        [{
        "epicTitle": "string — e.g. 'Calendar Phase 1: Grid.'",
        "currentHours": "number of current total work hours for the epic",
        "previousHours": "number of previous total work hours for the epic",
        "hoursDelta": "number",
        "currentContributors": "number of actors working on the epic",
        "previousContributors": "number of previous actors working on the epic",
        "currentCommitters": "number of commits to the epic",
        "previousCommitters": "number of previous commits to the epic",
        "committersDelta": "number of commits to the epic",
        "commentary": "string - a short commentary on the investment"
      }]
    }`,
  format: {
    type: "json",
    fields: ["executiveSummary", "highlights", "investments"],
    constraints: [
      { field: "executiveSummary", type: "string", required: true },
      { field: "highlights", type: "array", required: true },
      { field: "investments", type: "array", required: true },
    ],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data);
          return (
            Array.isArray(parsed) &&
            parsed.every(
              (row) =>
                row.epicTitle &&
                typeof row.currentHours === "number" &&
                typeof row.hoursDelta === "number" &&
                typeof row.contributors === "number" &&
                typeof row.contributorsDelta === "number"
            )
          );
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// --- Parser ---
function investmentSummaryParser(data: any) {
  try {
    return {
      data: data[investmentSummaryBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse investment summary data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// --- Variants ---
const investmentSummaryVariants = [
  {
    id: "table",
    name: "Table",
    description: "Table layout for investment by epic",
    settings: {},
  },
  {
    id: "top3-person-hours",
    name: "Top 3 by Person Hours",
    description: "Show top 3 investments by current person hours",
    settings: {},
  },
  {
    id: "top3-change-variance",
    name: "Top 3 by Change Variance",
    description: "Show top 3 investments by absolute change in person hours",
    settings: {},
  },
  {
    id: "categorized",
    name: "Categorized",
    description:
      "Show investments grouped by significant increases, sharp declines, and notable outliers",
    settings: {},
  },
  {
    id: "strategic-shifts",
    name: "Strategic Shifts & Deltas",
    description: "Show significant changes in effort and strategic focus",
    settings: {},
  },
  {
    id: "executive-summary",
    name: "Executive Summary",
    description: "Show executive summary with top 3 investments",
    settings: {},
  },
];

function renderRowDetails(row: any) {
  return (
    <div style={{ marginBottom: "var(--padding-4)" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <div>
          <Typography.Text
            className="vzbl-text-5"
            style={{ marginBottom: "var(--padding-2)" }}
          >
            {stripMarkdown(row.epicTitle)}
          </Typography.Text>
          <div className="vzbl-text-3">
            <Typography.Text
              className="vzbl-text-3"
              style={{
                fontWeight: "var(--font-weight-3)",
                paddingRight: "var(--padding-2)",
              }}
            >
              Hours:
            </Typography.Text>
            {row.currentHours.toFixed(2)} (current){" vs "}
            {row.previousHours.toFixed(2)} (previous) <br />
            <Typography.Text
              className="vzbl-text-3"
              style={{
                fontWeight: "var(--font-weight-3)",
                paddingRight: "var(--padding-2)",
              }}
            >
              Contributors:
            </Typography.Text>
            {row.currentContributors} (current){" vs "}
            {row.previousContributors} (previous)
          </div>
        </div>
        <div>
          <Tag className="vzbl-pill-tag" style={{ margin: "var(--padding-2)" }}>
            {row.hoursDelta > 0 ? (
              <ArrowUpOutlined />
            ) : row.hoursDelta < 0 ? (
              <ArrowDownOutlined />
            ) : (
              ""
            )}
            {Math.abs(row.hoursDelta).toFixed(2)}
          </Tag>
        </div>
      </div>

      {/* Add more details as needed */}
    </div>
  );
}

function InvestmentSummaryBlockRenderer(props: any) {
  const { data, className, style, variant } = props;
  const summaryData = data[investmentSummaryBlock.id] as InvestmentSummaryData;
  if (!summaryData || summaryData.investments?.length === 0) return null;

  if (variant === "executive-summary") {
    const hasExecutiveSummary = summaryData.executiveSummary;

    return (
      <div className="vzbl-panel-container">
        {hasExecutiveSummary && (
          <>
            <div style={{ paddingBottom: "var(--padding-4)" }}>
              <Typography.Text className="vzbl-header-4">
                Executive Summary
              </Typography.Text>
            </div>
            <div style={{ marginBottom: 24 }} className="vzbl-text-3">
              <Markdown remarkPlugins={[remarkGfm]} className="vzbl-text-3">
                {summaryData.executiveSummary}
              </Markdown>
            </div>
          </>
        )}
        {summaryData.highlights?.map((row, index) => {
          return (
            <div key={`highlight-${index}`} className="vzbl-text-3">
              <Markdown remarkPlugins={[remarkGfm]} className="vzbl-text-3">
                {row}
              </Markdown>
            </div>
          );
        })}
      </div>
    );
  }

  if (variant === "top3-person-hours") {
    const top3 = [...(summaryData.investments || [])]
      .sort((a, b) => b.currentHours - a.currentHours)
      .slice(0, 3);
    return (
      <div className="vzbl-panel-container">
        <Typography.Text className="vzbl-header-4">
          Top 3 Investments by Person Hours
        </Typography.Text>
        {top3.map((row) => renderRowDetails(row))}
      </div>
    );
  }

  if (variant === "top3-change-variance") {
    const top3 = [...(summaryData.investments || [])]
      .sort((a, b) => Math.abs(b.hoursDelta) - Math.abs(a.hoursDelta))
      .slice(0, 3);
    return (
      <div className="vzbl-panel-container">
        <div style={{ paddingBottom: "var(--padding-4)" }}>
          <Typography.Text className="vzbl-header-4">
            Top 3 Investments by Change Variance
          </Typography.Text>
        </div>
        {top3.map((row) => renderRowDetails(row))}
      </div>
    );
  }

  if (variant === "categorized") {
    // Define thresholds for significant increases/declines
    const threshold = Math.max(
      10,
      Math.round(
        0.2 *
          Math.max(
            ...(summaryData.investments || []).map((r) =>
              Math.abs(r.hoursDelta)
            )
          )
      )
    );
    const increases = (summaryData.investments || []).filter(
      (r) => r.hoursDelta >= threshold
    );
    const declines = (summaryData.investments || []).filter(
      (r) => r.hoursDelta <= -threshold
    );
    const outliers = (summaryData.investments || []).filter(
      (r) => Math.abs(r.hoursDelta) < threshold && Math.abs(r.hoursDelta) > 0
    );
    return (
      <div className="vzbl-panel-container">
        <Typography.Title level={5} style={{ marginBottom: 16 }}>
          <ArrowUpOutlined /> Significant Increases
        </Typography.Title>
        {increases.length > 0 ? (
          increases.map((row) => renderRowDetails(row))
        ) : (
          <div>No significant increases.</div>
        )}
        <Typography.Title level={5} style={{ margin: "24px 0 16px 0" }}>
          <ArrowDownOutlined /> Sharp Declines
        </Typography.Title>
        {declines.length > 0 ? (
          declines.map((row) => renderRowDetails(row))
        ) : (
          <div>No sharp declines.</div>
        )}
        <Typography.Title level={5} style={{ margin: "24px 0 16px 0" }}>
          <ExclamationCircleOutlined /> Notable Outliers
        </Typography.Title>
        {outliers.length > 0 ? (
          outliers.map((row) => renderRowDetails(row))
        ) : (
          <div>No notable outliers.</div>
        )}
      </div>
    );
  }

  if (variant === "strategic-shifts") {
    // 20% threshold for significant change
    const threshold = 0.2;
    const rows = (summaryData.investments || [])
      .filter((row) => {
        const prev = row.previousHours || 1;
        const percentChange = Number(
          ((row.currentHours - prev) / prev).toFixed(2)
        );
        return Math.abs(percentChange) >= threshold;
      })
      .map((row) => {
        const prev = row.previousHours || 1;
        const percentChange = Number(
          ((row.currentHours - prev) / prev).toFixed(2)
        );
        return {
          ...row,
          percentChange,
          absolutePercentChange: Math.abs(percentChange),
        };
      })
      .sort((a, b) => b.absolutePercentChange - a.absolutePercentChange)
      .slice(0, 5);

    return (
      <div className="vzbl-panel-container">
        <div style={{ paddingBottom: "var(--padding-4)" }}>
          <Typography.Text className="vzbl-header-5">
            Strategic Shifts & Deltas
          </Typography.Text>
        </div>
        {rows.length === 0 && <div>No significant shifts detected.</div>}
        {rows.map((row) => {
          const prev = row.previousHours || 1;
          const percentChange = row.percentChange;
          const isIncrease = percentChange > 0;
          return (
            <div
              key={row.epicTitle}
              style={{ marginBottom: "var(--padding-4)" }}
            >
              <Typography.Text
                className="vzbl-text-4"
                style={{ marginBottom: "var(--padding-2)" }}
              >
                {stripMarkdown(row.epicTitle)}
              </Typography.Text>
              <div>
                <Typography.Text
                  className="vzbl-text-3"
                  style={{ fontWeight: "var(--font-weight-3)" }}
                >
                  {isIncrease ? "+" : ""}
                  {Number(row.currentHours - prev).toFixed(2)} shift in
                  person-hours ({isIncrease ? "+" : ""}
                  {percentChange * 100}%).toFixed(2)
                </Typography.Text>
              </div>
              {row.commentary && (
                <>
                  <Markdown remarkPlugins={[remarkGfm]} className="vzbl-text-3">
                    {row.commentary}
                  </Markdown>
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Default: Table
  return (
    <>
      {summaryData &&
        summaryData.investments &&
        summaryData.investments.length > 0 && (
          <div className="vzbl-panel-container">
            <Typography.Text className="vzbl-header-4">
              Investment Details
            </Typography.Text>
            <Table
              dataSource={summaryData.investments || []}
              pagination={false}
              rowKey={(row) => row.epicTitle}
              size="small"
              style={{ background: "none" }}
              columns={[
                {
                  title: "Epic Title",
                  dataIndex: "epicTitle",
                  key: "epicTitle",
                  sorter: (a, b) => a.epicTitle.localeCompare(b.epicTitle),
                  render: (text: string) => (
                    <Markdown
                      remarkPlugins={[remarkGfm]}
                      className="vzbl-text-3"
                    >
                      {text}
                    </Markdown>
                  ),
                },
                {
                  title: "Current Hours",
                  dataIndex: "currentHours",
                  key: "currentHours",
                  align: "right" as const,
                  sorter: (a, b) => a.currentHours - b.currentHours,
                  render: (value: number) => (
                    <Typography.Text className="vzbl-text-3">
                      {Number(value).toFixed(2)}
                    </Typography.Text>
                  ),
                },
                {
                  title: "Previous Hours",
                  dataIndex: "previousHours",
                  key: "previousHours",
                  align: "right" as const,
                  sorter: (a, b) => a.previousHours - b.previousHours,
                  render: (value: number) => (
                    <Typography.Text className="vzbl-text-3">
                      {Number(value).toFixed(2)}
                    </Typography.Text>
                  ),
                },
                {
                  title: "Current Contributors",
                  dataIndex: "currentContributors",
                  key: "contributors",
                  align: "right" as const,
                  sorter: (a, b) =>
                    a.currentContributors - b.currentContributors,
                  render: (value: number) => (
                    <Typography.Text className="vzbl-text-3">
                      {value}
                    </Typography.Text>
                  ),
                },
                {
                  title: "Previous Contributors",
                  dataIndex: "previousContributors",
                  key: "previousContributors",
                  align: "right" as const,
                  sorter: (a, b) =>
                    a.previousContributors - b.previousContributors,
                  render: (value: number) => (
                    <Typography.Text className="vzbl-text-3">
                      {value}
                    </Typography.Text>
                  ),
                },
              ]}
            />
          </div>
        )}
    </>
  );
}

// --- Block Registration ---
const investmentSummaryBlock = createBaseBlock({
  id: "investment-summary",
  name: "Investment Summary Block",
  description:
    "Displays a table of investment by epic, person days, and contributors.",
  recipe: investmentSummaryRecipe,
  variants: investmentSummaryVariants,
  parser: investmentSummaryParser,
  renderer: InvestmentSummaryBlockRenderer,
});

export { investmentSummaryBlock };
